import { motion } from "framer-motion";
import { Download, BookOpen, Image as ImageIcon, Video } from "lucide-react";
import { PublicationCard } from "@/components/Cards";
import { IMAGES } from "@/assets/images";

interface Magazine {
  id: string;
  title: string;
  issue: string;
  date: string;
  image: string;
  downloadUrl: string;
}

interface Guide {
  id: string;
  title: string;
  description: string;
  image: string;
  downloadUrl: string;
}

interface Gallery {
  id: string;
  title: string;
  date: string;
  images: string[];
}

interface VideoItem {
  id: string;
  title: string;
  date: string;
  thumbnail: string;
  videoUrl: string;
}

const MAGAZINES: Magazine[] = [
  {
    id: "mag-2026-01",
    title: "Gréasque Mag",
    issue: "N°1 - Janvier 2026",
    date: "2026-01-15",
    image: IMAGES.MAIRIE_8,
    downloadUrl: "#",
  },
  {
    id: "mag-2025-12",
    title: "Gréasque Mag",
    issue: "N°12 - Décembre 2025",
    date: "2025-12-15",
    image: IMAGES.VILLAGE_4,
    downloadUrl: "#",
  },
  {
    id: "mag-2025-11",
    title: "Gréasque Mag",
    issue: "N°11 - Novembre 2025",
    date: "2025-11-15",
    image: IMAGES.EVENEMENT_1,
    downloadUrl: "#",
  },
  {
    id: "mag-2025-10",
    title: "Gréasque Mag",
    issue: "N°10 - Octobre 2025",
    date: "2025-10-15",
    image: IMAGES.MAIRIE_8,
    downloadUrl: "#",
  },
  {
    id: "mag-2025-09",
    title: "Gréasque Mag",
    issue: "N°9 - Septembre 2025",
    date: "2025-09-15",
    image: IMAGES.VILLAGE_4,
    downloadUrl: "#",
  },
  {
    id: "mag-2025-08",
    title: "Gréasque Mag",
    issue: "N°8 - Août 2025",
    date: "2025-08-15",
    image: IMAGES.EVENEMENT_1,
    downloadUrl: "#",
  },
];

const GUIDES: Guide[] = [
  {
    id: "guide-pratique",
    title: "Guide Pratique du Citoyen",
    description: "Toutes les démarches administratives et services municipaux",
    image: IMAGES.MAIRIE_8,
    downloadUrl: "#",
  },
  {
    id: "guide-associations",
    title: "Guide des Associations",
    description: "Annuaire complet des associations gréasquoises",
    image: IMAGES.VILLAGE_4,
    downloadUrl: "#",
  },
  {
    id: "guide-culture",
    title: "Guide Culture & Loisirs",
    description: "Programmation culturelle et activités de loisirs",
    image: IMAGES.EVENEMENT_1,
    downloadUrl: "#",
  },
];

const GALLERIES: Gallery[] = [
  {
    id: "gallery-1",
    title: "Salon du Livre 2025",
    date: "2025-11-15",
    images: [IMAGES.CULTURE_1, IMAGES.CULTURE_2, IMAGES.CULTURE_3, IMAGES.CULTURE_4],
  },
  {
    id: "gallery-2",
    title: "Fête de la Musique 2025",
    date: "2025-06-21",
    images: [IMAGES.EVENEMENT_1, IMAGES.EVENEMENT_2, IMAGES.EVENEMENT_3, IMAGES.EVENEMENT_4],
  },
  {
    id: "gallery-3",
    title: "Cérémonie du 11 Novembre",
    date: "2025-11-11",
    images: [IMAGES.PLACE_1, IMAGES.PLACE_2, IMAGES.PLACE_3, IMAGES.PLACE_4],
  },
];

const VIDEOS: VideoItem[] = [
  {
    id: "video-1",
    title: "Vœux du Maire 2026",
    date: "2026-01-10",
    thumbnail: IMAGES.MAIRIE_8,
    videoUrl: "#",
  },
  {
    id: "video-2",
    title: "Inauguration Centre Culturel",
    date: "2025-09-15",
    thumbnail: IMAGES.VILLAGE_4,
    videoUrl: "#",
  },
  {
    id: "video-3",
    title: "Fête Nationale 14 Juillet",
    date: "2025-07-14",
    thumbnail: IMAGES.EVENEMENT_1,
    videoUrl: "#",
  },
];

export default function Publications() {
  return (
    <div className="min-h-screen bg-background">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="relative py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Publications Municipales
              </h1>
              <p className="text-lg text-muted-foreground">
                Retrouvez tous les magazines, guides pratiques, galeries photos et vidéos de la ville de Gréasque
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-8">
                <BookOpen className="w-8 h-8 text-primary" />
                <h2 className="text-3xl font-bold text-foreground">Gréasque Mag</h2>
              </div>
              <p className="text-muted-foreground mb-8">
                Le magazine municipal de Gréasque vous informe chaque mois de l'actualité de votre ville
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {MAGAZINES.map((magazine, index) => (
                  <motion.div
                    key={magazine.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                  >
                    <PublicationCard
                      title={magazine.title}
                      issue={magazine.issue}
                      image={magazine.image}
                      downloadUrl={magazine.downloadUrl}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <Download className="w-8 h-8 text-primary" />
                <h2 className="text-3xl font-bold text-foreground">Guides Pratiques</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {GUIDES.map((guide, index) => (
                  <motion.div
                    key={guide.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                    className="bg-card rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={guide.image}
                        alt={guide.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-foreground mb-2">{guide.title}</h3>
                      <p className="text-muted-foreground mb-4">{guide.description}</p>
                      <a
                        href={guide.downloadUrl}
                        className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Télécharger le guide
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <ImageIcon className="w-8 h-8 text-primary" />
                <h2 className="text-3xl font-bold text-foreground">Galeries Photos</h2>
              </div>
              <div className="space-y-12">
                {GALLERIES.map((gallery, galleryIndex) => (
                  <motion.div
                    key={gallery.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * galleryIndex }}
                    className="bg-card rounded-xl p-6 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-2xl font-semibold text-foreground mb-1">{gallery.title}</h3>
                        <p className="text-muted-foreground">
                          {new Date(gallery.date).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {gallery.images.map((image, imageIndex) => (
                        <motion.div
                          key={imageIndex}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: 0.05 * imageIndex }}
                          className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:scale-105 transition-transform"
                        >
                          <img
                            src={image}
                            alt={`${gallery.title} - Photo ${imageIndex + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <Video className="w-8 h-8 text-primary" />
                <h2 className="text-3xl font-bold text-foreground">Vidéos</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {VIDEOS.map((video, index) => (
                  <motion.div
                    key={video.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                    className="bg-card rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                  >
                    <div className="aspect-video overflow-hidden relative">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                          <Video className="w-8 h-8 text-primary-foreground" />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-foreground mb-2">{video.title}</h3>
                      <p className="text-muted-foreground text-sm">
                        {new Date(video.date).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </div>
  );
}